/**
 * App payload carried INSIDE the protocol-v2 ratchet — never visible to relays.
 *
 * v1 left the content type in cleartext on the wire and used the NIP-01 event
 * hash as the recipient's message id, while the sender kept its own random id:
 * replies, reactions, edits and deletes referenced ids the other side never had.
 * v2 carries the sender's stable message id (and creation time) end-to-end.
 */
import { z } from "zod";

export const APP_PAYLOAD_VERSION = 1;

export const AppPayloadSchema = z
  .object({
    v: z.literal(APP_PAYLOAD_VERSION),
    /** Content type (CONTENT_TYPE_*). */
    t: z.number().int().min(1).max(64),
    /** Stable message id shared by both parties (64 lowercase hex). */
    id: z.string().regex(/^[0-9a-f]{64}$/),
    /** Sender clock, unix ms (display only — clamp with `boundedSenderTime`). */
    at: z.number().int().nonnegative(),
    /** Body (text, JSON op, attachment manifest …). */
    b: z.string().max(4_000_000),
    /**
     * Sender's chosen display name — only used to label a first-contact message
     * request (it used to ride in CLEARTEXT on the relay envelope in v1).
     */
    n: z.string().max(64).optional(),
  })
  .strict();

export type AppPayload = z.infer<typeof AppPayloadSchema>;

const enc = new TextEncoder();

export function encodeAppPayload(p: AppPayload): Uint8Array {
  return enc.encode(JSON.stringify(AppPayloadSchema.parse(p)));
}

/** Throws on malformed / non-UTF-8 / schema-violating payloads. */
export function decodeAppPayload(bytes: Uint8Array): AppPayload {
  const text = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  return AppPayloadSchema.parse(JSON.parse(text));
}

const MAX_FUTURE_SKEW_MS = 5 * 60_000;
const MAX_PAST_MS = 45 * 24 * 60 * 60_000;

/**
 * Sender clocks are claims: keep display times plausible so a peer cannot pin a
 * message to the top/bottom of a chat with absurd timestamps.
 */
export function boundedSenderTime(at: number, receivedAt = Date.now()): number {
  if (!Number.isFinite(at)) return receivedAt;
  return Math.min(Math.max(at, receivedAt - MAX_PAST_MS), receivedAt + MAX_FUTURE_SKEW_MS);
}
